/**
 * AI Art Director — review of a GENERATED Design JSON V2 document.
 *
 * `reviewCreativePlan` asks whether the thinking was worth building.
 * This asks the question that only the finished document can answer: is what
 * came back actually a design?
 *
 * It shares the scoring vocabulary, severity weights and approval thresholds
 * with the plan review, so "approved" means the same thing at both gates and a
 * score is comparable across them. It returns the same review shape, so the
 * results screen and the improvement loop read it without special-casing.
 *
 * Pure and deterministic. No AI, no OpenAI, no renderer, no editor.
 */

import { computeScores } from './score';
import { DOCUMENT_RULES, allNodes, allSections } from './documentRules';
import { expectationFor } from './compositionExpectations';
import { visualSignals } from './visualSignals';
import {
  REVIEW_SCHEMA_ID, REVIEW_VERSION, SEVERITY, SEVERITY_ORDER, DEFAULT_THRESHOLDS, VISUAL_THRESHOLDS,
} from './reviewSchema';

const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});

/** A one-line verdict, in the same voice as the plan review's summary. */
const summarize = (approved, scores, findings, nodeCount) => {
  if (approved) {
    return `Approved. ${nodeCount} nodes, creative ${scores.creative}, marketplace ${scores.marketplace}.`;
  }
  const blockers = findings.filter((f) => f.severity === SEVERITY.BLOCKER).length;
  return blockers
    ? `Rejected on ${blockers} blocking issue${blockers === 1 ? '' : 's'}: ${findings.filter((f) => f.severity === SEVERITY.BLOCKER).map((f) => f.detects).join(', ')}.`
    : `Sent back for revision: ${findings.map((f) => f.detects).join(', ')}.`;
};

/**
 * Review a generated Design JSON V2 document.
 *
 * @param {Object} document        the Design JSON V2 the model returned.
 * @param {Object} [context]       { plan, research, genome, rendered }.
 *   `rendered` is the Rendering Engine's output for this document. Supply it
 *   and the visual rules run — hierarchy, balance, focal mass — judging what
 *   the page looks like rather than only what it declares. Without it the
 *   review is structural only, which is correct for a caller that has no
 *   geometry yet.
 * @param {Object} [options]
 * @param {Object} [options.thresholds] { creative, marketplace }.
 * @returns {Object} the same review shape as reviewCreativePlan.
 */
export const reviewDesignDocument = (document, context = {}, options = {}) => {
  // A review that saw the page is held to the art-directed bar; one that saw
  // only the document is held to the structural bar. An explicit threshold
  // from the caller still wins over both.
  const base = context.rendered ? VISUAL_THRESHOLDS : DEFAULT_THRESHOLDS;
  const thresholds = { ...base, ...asObject(options.thresholds) };
  // The document type decides what "composed" means. It comes from the brief
  // when the caller supplies one, and from the document's own meta otherwise.
  const documentType = asObject(context.plan).meta?.documentType
    || asObject(asObject(document).meta).documentType
    || asObject(asObject(context.research).request).documentType
    || null;

  const ctx = {
    documentType,
    expectation: expectationFor(documentType),
    visuals: context.rendered ? visualSignals(context.rendered) : null,
    // What the image resolver actually found. Absent when a caller reviews a
    // document without running the image flow, and the imagery rules stay quiet.
    assets: context.assets ? asObject(context.assets) : null,
    document: asObject(document),
    nodes: allNodes(document),
    sections: allSections(document),
    plan: asObject(context.plan),
    research: asObject(context.research),
    genome: asObject(context.genome),
  };

  const findings = [];
  const strengths = [];

  DOCUMENT_RULES.forEach((rule) => {
    const result = asObject(rule.check(ctx));
    if (result.issue) {
      findings.push({
        rule: rule.id,
        reviews: rule.reviews,
        detects: rule.detects,
        severity: rule.severity,
        scores: rule.scores,
        message: rule.weakness(ctx, result.detail),
        request: rule.revision(ctx, result.detail),
      });
      return;
    }
    if (rule.strength) {
      const note = rule.strength(ctx);
      if (note) strengths.push(note);
    }
  });

  const ranked = [...findings].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity));
  const scores = computeScores(findings);
  const blocking = findings.some((f) => f.severity === SEVERITY.BLOCKER);
  const approved = !blocking && scores.creative >= thresholds.creative && scores.marketplace >= thresholds.marketplace;

  return {
    schema: REVIEW_SCHEMA_ID,
    version: REVIEW_VERSION,
    target: 'document',
    approved,
    rejected: !approved,
    review: summarize(approved, scores, ranked, ctx.nodes.length),
    scores,
    strengths,
    weaknesses: ranked.map(({ rule, reviews, detects, severity, message }) => ({ rule, reviews, detects, severity, message })),
    // On rejection these become the corrections sent back to the model.
    revisionRequests: approved
      ? []
      : ranked.map(({ rule, reviews, detects, severity, request, message }) => ({
        rule, areas: reviews, detects, severity, request, message,
      })),
    meta: {
      documentType,
      expectation: ctx.expectation,
      // What the page measured, so a rejection can be checked rather than trusted.
      visuals: ctx.visuals && {
        largestFontSize: ctx.visuals.largestFontSize,
        displayRatio: ctx.visuals.displayRatio,
        dominanceOverNext: ctx.visuals.dominanceOverNext,
        dominanceOverMetadata: ctx.visuals.dominanceOverMetadata,
        meanCoverage: ctx.visuals.occupancy.meanCoverage,
        emptyBandRatio: ctx.visuals.occupancy.emptyBandRatio,
        emptyRunAtEdge: ctx.visuals.occupancy.emptyRunAtEdge,
        focal: ctx.visuals.focal,
      },
      judgedVisually: Boolean(ctx.visuals),
      nodeCount: ctx.nodes.length,
      sectionCount: ctx.sections.length,
      rulesRun: DOCUMENT_RULES.length,
      thresholds,
    },
  };
};
